export interface IFilter {
    filter: string;
    attribute?: string;
    utc?: boolean;
    withCriteriaName?: string;
    withCriteriaValue?: string[];
}

export const BooleanKeys = {
    NONE: 'NONE',
    TRUE: 'TRUE',
    FALSE: 'FALSE'
} as const;

export type BooleanKeysType = (typeof BooleanKeys)[keyof typeof BooleanKeys];

export interface IBooleanOption {
    label: string;
    value?: boolean;
}

export const FILTERS = {
    APPLICATIONS: 'applications',
    ASSET_TYPES: 'assetTypes',
    BRIEF: 'brief',
    BRIEFS: 'briefs',
    BRIEF_GROUPS: 'briefGroup',
    BUSINESS_UNIT: 'businessUnit',
    CATEGORIES: 'categories',
    CLINICAL_INDICATIONS: 'clinicalIndications',
    COMPANY_TYPES: 'companyTypes',
    COMPANY: 'company',
    COUNTRIES: 'countries',
    CREATED_BY: 'createdBy',
    CREATED_ON_BEGIN: 'createdOnBegin',
    CREATED_ON_END: 'createdOnEnd',
    CONTACT_TAGS: 'contactTags',
    DEAL_STAGES: 'dealStages',
    DEAL_STATE: 'dealState',
    DEAL_TYPE: 'dealType',
    DEVELOPMENT_PHASES: 'developmentPhases',
    DISCOVERY_DATE_BEGIN: 'discoveryDateBegin',
    DISCOVERY_DATE_END: 'discoveryDateEnd',
    DISQUALIFIED_REASON: 'disqualifiedReason',
    IS_CDA: 'isCDA',
    LAST_MEETING_DATE_BEGIN: 'lastMeetingDateBegin',
    LAST_MEETING_DATE_END: 'lastMeetingDateEnd',
    LEAD_ASSET_CATEGORY: 'leadAssetCategory',
    LEAD_STAGES: 'leadStages',
    LEAD_STATUS: 'statusTab',
    LEAD_CONNECTION_STATUS: 'connectionStatus',
    LEAD_SUBMISSION_TYPE: 'leadSubmissionType',
    MECHANISM_ACTIONS: 'mechanismActions',
    MODIFIED_BY: 'modifiedBy',
    MODIFIED_DATE_BEGIN: 'modifiedDateBegin',
    MODIFIED_DATE_END: 'modifiedDateEnd',
    ON_HOLD_DATE_BEGIN: 'onHoldDateBegin',
    ON_HOLD_DATE_END: 'onHoldDateEnd',
    ON_HOLD_REASON: 'onHoldReason',
    ONLY_CURRENT_USER_BRIEF: 'onlyUserBrief',
    ORIGIN_ENTITY: 'sourceName',
    ORIGIN: 'source',
    OWNERS: 'owners',
    RELATED_COMPANIES: 'relatedCompanies',
    SEARCH_TEXT: 'searchText',
    THERAPEUTIC_AREAS: 'therapeuticAreas',
    TECHNOLOGIES: 'technologies',
    COMPANY_NAME: 'companySearchText',
    TECHNOLOGY_TYPES: 'technologyTypes',
    STAGES: 'stages',
    TAGS: 'tags',
    TERRITORIES: 'territories',
    TIMESTAMP: 'timestamp',
    WORKFLOW_TYPES: 'workflowTypes',
    WORKFLOW_STAGES: 'workflowStages',
    // contact filters
    CITY: 'city',
    COMPANIES: 'companies',
    COUNTRY: 'country',
    CREATION_DATE_BEGIN: 'creationDateBegin',
    CREATION_DATE_END: 'creationDateEnd',
    JOB_TITLE_SEARCH_TEXT: 'jobTitleSearchText',
    LEADS: 'leads',
    AGREEMENTS: 'agreements',
    ALLIANCES: 'alliances',
    NAME_SEARCH_TEXT: 'nameSearchText',

    // amendment filters
    AMENDMENT: {
        SEARCH_TEXT: 'amendmentSearchText',
        AGREEMENT: 'agreement',
        AMENDED_SECTIONS: 'amendedSections'
    },
    // obligation filters
    OBLIGATION_INSTANCE: {
        OBLIGATION: 'obligation',
        NOT_RECURRING: 'notRecurring',
        NAME_SEARCH_TEXT: 'obligationNameSearch',
        SEARCH_TEXT: 'obligationSearchText',
        TYPES: 'obligationTypes',
        STATUSES: 'obligationStatuses',
        OWNER: 'owner',
        RESPONSIBILITY_TYPE: 'obligationResponsibilityTypes',
        RESPONSIBLE_PARTY: 'obligationParty',
        DUE_DATE: {
            FROM: 'obligationDueDateFrom',
            TO: 'obligationDueDateTo'
        },
        ORIGINAL_DATE: {
            FROM: 'obligationOriginalEndDateFrom',
            TO: 'obligationOriginalEndDateTo'
        },
        AMOUNT: {
            MIN: 'obligationAmountMin',
            MAX: 'obligationAmountMax'
        },
        OBLIGATION_ID: 'obligationId',
        TERRITORIES: 'obligationTerritories',
        AGREEMENT: 'agreement',
        ALLIANCE: 'alliance',
        TERMINATE: 'terminateWithAgreement',
        TRIGGERING_EVENT: 'triggeringEvent'
    }
} as const;

export const LIST_SETTINGS = {
    VIEW: 'view'
};

export const FILTERS_MAP = {
    IS_IN_STRING_LIST: [
        { filter: FILTERS.LEAD_STATUS, attribute: 'status' },
        { filter: FILTERS.LEAD_CONNECTION_STATUS, attribute: 'originInfo.leadOriginProfile.status' },
        { filter: FILTERS.LEAD_ASSET_CATEGORY, attribute: 'assetProfiles.assetCategory' }
    ],
    IS_IN: [
        { filter: FILTERS.CREATED_BY, attribute: 'functionalCreatedBy' },
        { filter: FILTERS.DISQUALIFIED_REASON, attribute: 'statusInfo.disqualifiedReason' },
        { filter: FILTERS.MODIFIED_BY, attribute: 'modifiedBy' },
        { filter: FILTERS.OWNERS, attribute: 'accessControlInfo.functionalOwner' },
        { filter: FILTERS.ON_HOLD_REASON, attribute: 'statusInfo.onHoldReason' },
        { filter: FILTERS.TAGS, attribute: '@(:LeadTag:onLeads)' },
        { filter: FILTERS.DEVELOPMENT_PHASES, attribute: 'assetProfileEntity.developmentPhase' },
        { filter: FILTERS.ASSET_TYPES, attribute: 'assetProfileEntity.assetTypes' },
        { filter: FILTERS.MECHANISM_ACTIONS, attribute: 'assetProfileEntity.mechanismOfActions' },
        { filter: FILTERS.THERAPEUTIC_AREAS, attribute: 'assetProfileEntity.therapeuticAreas' },
        { filter: FILTERS.CLINICAL_INDICATIONS, attribute: 'assetProfileEntity.clinicalIndications' },
        { filter: FILTERS.TECHNOLOGIES, attribute: 'assetProfileEntity.technologies' },
        { filter: FILTERS.COMPANY_TYPES, attribute: 'company.companyTypes' },
        { filter: FILTERS.COUNTRIES, attribute: 'company.address.country' },
        { filter: FILTERS.BRIEFS, attribute: 'brief' },
        { filter: FILTERS.BRIEF_GROUPS, attribute: 'brief.group' },
        { filter: FILTERS.DEAL_STATE, attribute: 'currentStatusUpdateDealState' },
        { filter: FILTERS.TECHNOLOGY_TYPES, attribute: 'assetProfileEntity.technologyTypes' },
        { filter: FILTERS.TERRITORIES, attribute: 'territory.countries' },
        { filter: FILTERS.CATEGORIES, attribute: 'assetProfileEntity.categories' }
    ],
    EQUAL_TO_TYPE_IS: [{ filter: FILTERS.ORIGIN_ENTITY, attribute: 'originInfo.originEntity' }],
    EQUAL_TO: [{ filter: FILTERS.ORIGIN, attribute: 'origin' }],
    AFTER_OR_EQUAL_TO: [
        { filter: FILTERS.ON_HOLD_DATE_BEGIN, attribute: 'statusInfo.onHoldDueDate', utc: true },
        { filter: FILTERS.DISCOVERY_DATE_BEGIN, attribute: 'discoveredOn', utc: true },
        { filter: FILTERS.MODIFIED_DATE_BEGIN, attribute: 'modifiedOn', utc: false },
        { filter: FILTERS.LAST_MEETING_DATE_BEGIN, attribute: 'lastMeetingDate', utc: true },
        { filter: FILTERS.CREATED_ON_BEGIN, attribute: 'functionalCreatedOn', utc: true }
    ],
    BEFORE_OR_EQUAL_TO: [
        { filter: FILTERS.ON_HOLD_DATE_END, attribute: 'statusInfo.onHoldDueDate', utc: true },
        { filter: FILTERS.DISCOVERY_DATE_END, attribute: 'discoveredOn', utc: true },
        { filter: FILTERS.MODIFIED_DATE_END, attribute: 'modifiedOn', utc: false },
        { filter: FILTERS.LAST_MEETING_DATE_END, attribute: 'lastMeetingDate', utc: true },
        { filter: FILTERS.CREATED_ON_END, attribute: 'functionalCreatedOn', utc: true }
    ],
    FULL_TEXT_SEARCH: [{ filter: FILTERS.SEARCH_TEXT }],
    SIMPLE_TEXT_SEARCH: [{ filter: FILTERS.LEAD_SUBMISSION_TYPE, attribute: 'originInfo.leadOriginProfile.type' }]
};

export const CONTACT_FILTERS_MAP = {
    BUSINESS_UNIT: { filter: FILTERS.BUSINESS_UNIT, attribute: 'businessUnit' },
    NAME: { filter: FILTERS.NAME_SEARCH_TEXT, attribute: 'dobjPrettifiedBusinessWiseId' },
    CREATION_DATE_BEGIN: { filter: FILTERS.CREATION_DATE_BEGIN, attribute: 'createdOn', utc: false },
    CREATION_DATE_END: { filter: FILTERS.CREATION_DATE_END, attribute: 'createdOn', utc: false },
    COMPANIES: { filter: FILTERS.COMPANIES, attribute: 'company' },
    COUNTRY: { filter: FILTERS.COUNTRY, attribute: 'address.country.dobjId' },
    CITY: { filter: FILTERS.CITY, attribute: 'address.city' },
    LEADS: { filter: FILTERS.LEADS, attribute: 'lead' },
    JOB_TITLE_SEARCH_TEXT: { filter: FILTERS.JOB_TITLE_SEARCH_TEXT, attribute: 'jobTitle' },
    CONTACT_TAGS: { filter: FILTERS.CONTACT_TAGS },
    AGREEMENTS: { filter: FILTERS.AGREEMENTS, attribute: 'agreement' },
    ALLIANCES: { filter: FILTERS.ALLIANCES, attribute: 'alliance' }
};

export const INTERNAL_CONTACT_FILTERS_MAP = {
    NAME: { filter: FILTERS.NAME_SEARCH_TEXT, attribute: 'dobjPrettifiedBusinessWiseId' }
};

export const opportunityActivityFilters = [
    FILTERS.CREATED_ON_BEGIN,
    FILTERS.CREATED_ON_END,
    FILTERS.CREATED_BY,
    FILTERS.DISQUALIFIED_REASON,
    FILTERS.MODIFIED_BY,
    FILTERS.ON_HOLD_REASON,
    FILTERS.TERRITORIES,
    FILTERS.DISCOVERY_DATE_BEGIN,
    FILTERS.DISCOVERY_DATE_END,
    FILTERS.MODIFIED_DATE_BEGIN,
    FILTERS.MODIFIED_DATE_END,
    FILTERS.LAST_MEETING_DATE_BEGIN,
    FILTERS.LAST_MEETING_DATE_END
];

export const opportunityDetailsFilters = [
    FILTERS.LEAD_ASSET_CATEGORY,
    FILTERS.ORIGIN_ENTITY,
    FILTERS.ORIGIN,
    FILTERS.LEAD_CONNECTION_STATUS,
    FILTERS.LEAD_SUBMISSION_TYPE
];

export const companyDetailsFilters = [FILTERS.COMPANY_TYPES, FILTERS.COUNTRIES, FILTERS.RELATED_COMPANIES];
